import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { UserRole, User } from '../types';
import apiService from '../services/api';
import './UserManagement.css';

// Maximum number of Administrator accounts allowed
const MAX_ADMIN_ACCOUNTS = 5;

const UserManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | 'All'>('All');
  const [userSearch, setUserSearch] = useState('');
  const [sortField, setSortField] = useState<'name' | 'email' | 'status' | 'created' | 'lastLogin'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [emailEditUser, setEmailEditUser] = useState<User | null>(null);
  const [passwordEditUser, setPasswordEditUser] = useState<User | null>(null);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordNewPassword, setResetPasswordNewPassword] = useState('');
  const [resetPasswordConfirmPassword, setResetPasswordConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordStep, setResetPasswordStep] = useState<'email' | 'password' | 'confirm'>('email');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Unlock user modal states
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockUser, setUnlockUser] = useState<User | null>(null);
  const [unlockNewPassword, setUnlockNewPassword] = useState('');
  const [unlockConfirmPassword, setUnlockConfirmPassword] = useState('');
  const [showUnlockPassword, setShowUnlockPassword] = useState(false);
  const [showUnlockConfirmPassword, setShowUnlockConfirmPassword] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlockIsLoading, setUnlockIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    user_number: '',
    name: '',
    email: '',
    role: 'Application Owner' as UserRole,
    status: 'Active' as 'Active' | 'Inactive',
    password: ''
  });

  // Fetch users from database on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const fetchedUsers = await apiService.getUsers();
        setUsers(fetchedUsers);
      } catch (error) {
        alert('Failed to load users from database');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Check if current user is an Administrator
  const isAdmin = currentUser?.role === 'Administrator';

  // Filter users based on role-based access
  // Administrators see all users, others see only themselves
  const accessibleUsers = isAdmin
    ? users
    : users.filter(u => u.id === currentUser?.id);

  const filteredUsers = accessibleUsers.filter(user => {
    // When searching, ignore role filter to search across all users
    const roleMatch = userSearch.trim() ? true : (selectedRole === 'All' || user.role === selectedRole);
    if (!roleMatch) return false;
    if (!userSearch.trim()) return true;
    const s = userSearch.toLowerCase();
    return (user.name || '').toLowerCase().includes(s) || (user.email || '').toLowerCase().includes(s);
  });

  // Group filtered users by role for tabular display
  const groupedUsers = {
    'Administrator': filteredUsers.filter(u => u.role === 'Administrator'),
    'Application Owner': filteredUsers.filter(u => u.role === 'Application Owner'),
    'APR Analyst': filteredUsers.filter(u => u.role === 'APR Analyst')
  };

  // Get locked users
  const lockedUsers = accessibleUsers.filter(u => (u as any).is_locked === true);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // APR Analyst cannot create or edit users with Administrator role
    if (currentUser?.role === 'APR Analyst' && formData.role === 'Administrator') {
      alert('APR Analysts cannot create or modify Administrator users');
      return;
    }

    // Check Administrator limit (max 2 accounts, excluding system account)
    if (formData.role === 'Administrator') {
      const currentAdminCount = users.filter(
        u => u.role === 'Administrator' &&
             u.id !== editingUser?.id // Don't count the user being edited
      ).length;

      // If not editing an existing admin, check if we've reached the limit
      const isChangingToAdmin = !editingUser || editingUser.role !== 'Administrator';
      if (isChangingToAdmin && currentAdminCount >= MAX_ADMIN_ACCOUNTS) {
        alert(`Maximum of ${MAX_ADMIN_ACCOUNTS} Administrator accounts allowed. Please contact your system administrator.`);
        return;
      }
    }

    try {
      if (editingUser) {
        const updatedUser = await apiService.updateUser(editingUser.id, {
          user_number: editingUser.user_number,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          status: formData.status
        });
        setUsers(prev => prev.map(user => user.id === editingUser.id ? updatedUser : user));
      } else {
        // Auto-generate user number: find the max user_number and add 1
        const maxUserNumber = users.length > 0
          ? Math.max(...users.map(u => u.user_number))
          : 1000;
        const newUserNumber = maxUserNumber + 1;

        const newUser = await apiService.createUser({
          user_number: newUserNumber,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          status: formData.status,
          password: formData.password
        });
        setUsers(prev => [...prev, newUser]);
      }

      resetForm();
    } catch (error: any) {
      // Show specific error message from backend if available
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to save user. Please try again.';
      alert(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      user_number: '',
      name: '',
      email: '',
      role: 'Application Owner',
      status: 'Active',
      password: ''
    });
    setShowForm(false);
    setEditingUser(null);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      user_number: user.user_number.toString(),
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      password: ''
    });
    setShowForm(true);
  };

  const handleDelete = async (user: User) => {
    if (user.id === currentUser?.id) {
      alert('You cannot delete your own account.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${user.name}"?`)) {
      try {
        await apiService.deleteUser(user.id);
        setUsers(prev => prev.filter(u => u.id !== user.id));
      } catch (error) {
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleEmailEdit = (user: User) => {
    setEmailEditUser(user);
    setNewEmail(user.email);
    setShowEmailModal(true);
  };

  const handleEmailUpdate = async () => {
    if (emailEditUser && newEmail.trim()) {
      try {
        const updatedUser = await apiService.updateUser(emailEditUser.id, { email: newEmail.trim() });
        setUsers(prev => prev.map(user => user.id === emailEditUser.id ? updatedUser : user));
        setShowEmailModal(false);
        setEmailEditUser(null);
        setNewEmail('');
      } catch (error) {
        alert('Failed to update email. Please try again.');
      }
    }
  };

  const handlePasswordEdit = (user: User) => {
    // APR Analyst can only change password for Application Owner
    if (currentUser?.role === 'APR Analyst' && user.role !== 'Application Owner') {
      alert('APR Analysts can only change passwords for Application Owners');
      return;
    }

    setPasswordEditUser(user);
    setNewPassword('');
    setShowPassword(false);
    setShowPasswordModal(true);
  };

  const handlePasswordUpdate = async () => {
    if (passwordEditUser && newPassword.trim()) {
      try {
        const updatedUser = await apiService.updateUser(passwordEditUser.id, { password: newPassword.trim() });
        setUsers(prev => prev.map(user => user.id === passwordEditUser.id ? updatedUser : user));
        setShowPasswordModal(false);
        setPasswordEditUser(null);
        setNewPassword('');
        setShowPassword(false);
      } catch (error) {
        alert('Failed to update password. Please try again.');
      }
    }
  };

  const handleResetPasswordOpen = () => {
    setResetPasswordEmail('');
    setResetPasswordNewPassword('');
    setResetPasswordConfirmPassword('');
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
    setResetPasswordError('');
    setResetPasswordStep('email');
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordVerifyEmail = async () => {
    if (!resetPasswordEmail.trim()) {
      setResetPasswordError('Please enter an email address');
      return;
    }

    const user = users.find(u => u.email.toLowerCase() === resetPasswordEmail.trim().toLowerCase());
    if (!user) {
      setResetPasswordError('No user found with this email address');
      return;
    }

    setResetPasswordError('');
    setResetPasswordStep('password');
  };

  const handleResetPasswordNext = () => {
    if (!resetPasswordNewPassword.trim()) {
      setResetPasswordError('Please enter a new password');
      return;
    }
    setResetPasswordError('');
    setResetPasswordConfirmPassword('');
    setResetPasswordStep('confirm');
  };

  const handleResetPasswordBack = () => {
    if (resetPasswordStep === 'confirm') {
      setResetPasswordStep('password');
      setResetPasswordConfirmPassword('');
    } else if (resetPasswordStep === 'password') {
      setResetPasswordStep('email');
      setResetPasswordNewPassword('');
    }
    setResetPasswordError('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordEmail.trim()) {
      setResetPasswordError('Please enter an email address');
      return;
    }

    if (!resetPasswordNewPassword.trim()) {
      setResetPasswordError('Please enter a new password');
      return;
    }

    if (!resetPasswordConfirmPassword.trim()) {
      setResetPasswordError('Please confirm the new password');
      return;
    }

    if (resetPasswordNewPassword !== resetPasswordConfirmPassword) {
      setResetPasswordError('Passwords do not match. Please try again.');
      return;
    }

    const user = users.find(u => u.email.toLowerCase() === resetPasswordEmail.trim().toLowerCase());
    if (!user) {
      setResetPasswordError('No user found with this email address');
      return;
    }

    try {
      const updatedUser = await apiService.updateUser(user.id, { password: resetPasswordNewPassword.trim() });
      setUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
      setShowResetPasswordModal(false);
      setResetPasswordEmail('');
      setResetPasswordNewPassword('');
      setResetPasswordConfirmPassword('');
      setShowResetPassword(false);
      setShowResetConfirmPassword(false);
      setResetPasswordError('');
      alert('Password has been reset successfully!');
    } catch (error) {
      setResetPasswordError('Failed to reset password. Please try again.');
    }
  };

  const handleUnlockUser = (user: User) => {
    setUnlockUser(user);
    setUnlockNewPassword('');
    setUnlockConfirmPassword('');
    setUnlockError('');
    setShowUnlockPassword(false);
    setShowUnlockConfirmPassword(false);
    setShowUnlockModal(true);
  };

  const handleUnlockSubmit = async () => {
    if (!unlockUser) return;

    if (!unlockNewPassword.trim()) {
      setUnlockError('Please enter a new password for the user');
      return;
    }

    if (!unlockConfirmPassword.trim()) {
      setUnlockError('Please confirm the new password');
      return;
    }

    if (unlockNewPassword !== unlockConfirmPassword) {
      setUnlockError('Passwords do not match. Please try again.');
      return;
    }

    setUnlockIsLoading(true);
    setUnlockError('');

    try {
      // First unlock the user using the dedicated unlock endpoint
      const unlockedUser = await apiService.unlockUser(unlockUser.id);

      // Then set the new password
      const updatedUser = await apiService.updateUser(unlockUser.id, {
        password: unlockNewPassword.trim()
      });

      setUsers(prev => prev.map(u => u.id === unlockUser.id ? { ...updatedUser, is_locked: false } : u));

      setShowUnlockModal(false);
      setUnlockUser(null);
      setUnlockNewPassword('');
      alert(`Account for ${unlockUser.name} has been unlocked and password has been reset!`);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to unlock user. Please try again.';
      setUnlockError(errorMessage);
    } finally {
      setUnlockIsLoading(false);
    }
  };

  const handleUnlockCancel = () => {
    setShowUnlockModal(false);
    setUnlockUser(null);
    setUnlockNewPassword('');
    setUnlockConfirmPassword('');
    setUnlockError('');
    setShowUnlockPassword(false);
    setShowUnlockConfirmPassword(false);
  };

  const getRoleIcon = (role: UserRole) => {
    const icons = {
      'Administrator': '👑',
      'Application Owner': '👨‍💼',
      'APR Analyst': '📊'
    };
    return icons[role];
  };

  const getRoleClass = (role: UserRole) => {
    return role.toLowerCase().replace(' ', '-');
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortUsers = (userList: User[]): User[] => {
    return [...userList].sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      switch (sortField) {
        case 'name': valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase(); break;
        case 'email': valA = (a.email || '').toLowerCase(); valB = (b.email || '').toLowerCase(); break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'created': valA = a.createdDate || (a as any).created_date || ''; valB = b.createdDate || (b as any).created_date || ''; break;
        case 'lastLogin': valA = a.lastLogin || ''; valB = b.lastLogin || ''; break;
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortIcon = (field: typeof sortField) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const downloadUserData = () => {
    const headers = ['Name', 'Email', 'Role', 'Status', 'Created Date', 'Last Login'];
    const csvRows = [headers.join(',')];
    filteredUsers.forEach(user => {
      csvRows.push([
        `"${(user.name || '').replace(/"/g, '""')}"`,
        `"${(user.email || '').replace(/"/g, '""')}"`,
        `"${user.role}"`,
        `"${user.status}"`,
        `"${user.createdDate || (user as any).created_date || ''}"`,
        `"${user.lastLogin || 'Never'}"`
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_management_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const renderUserRow = (user: User) => (
    <tr key={user.id}>
      <td>
        <div className="user-info">
          <div className={`user-avatar ${user.status.toLowerCase()}`}>
            {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
            <div className={`user-status-indicator ${user.status.toLowerCase()}`}></div>
          </div>
          <div className="user-details">
            <h4>
              {user.name}
              {user.id === currentUser?.id && <span className="you-badge">You</span>}
            </h4>
            <div className="user-id">User #{user.user_number}</div>
          </div>
        </div>
      </td>
      <td>
        <a href={`mailto:${user.email}`} className="user-email">
          {user.email}
        </a>
      </td>
      <td>
        <span className={`status-badge ${user.status.toLowerCase()}`}>
          {user.status === 'Active' ? '✅' : '❌'} {user.status}
        </span>
      </td>
      <td>
        <div className="date-info">
          {formatDate(user.createdDate || (user as any).created_date || (user as any).createddate)}
        </div>
      </td>
      <td>
        <div className="date-info">
          {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
        </div>
      </td>
      <td>
        <div className="actions-group">
          {/* Admin-only actions: Edit, Email, Delete */}
          {isAdmin && (
            <>
              <button
                className="action-btn edit"
                onClick={() => handleEdit(user)}
                title="Edit user"
              >
                ✏️
              </button>
              <button
                className="action-btn email"
                onClick={() => handleEmailEdit(user)}
                title="Update email"
              >
                📧
              </button>
            </>
          )}
          {/* Password change: Admin can change any, non-admin can only change their own */}
          {(isAdmin || user.id === currentUser?.id) && (
            <button
              className="action-btn password"
              onClick={() => handlePasswordEdit(user)}
              title="Change password"
            >
              🔒
            </button>
          )}
          {/* Delete: Admin only */}
          {isAdmin && (
            <button
              className="action-btn delete"
              onClick={() => handleDelete(user)}
              disabled={user.id === currentUser?.id}
              title={user.id === currentUser?.id ? "Cannot delete your own account" : "Delete user"}
            >
              🗑️
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderRoleSection = (role: UserRole, users: User[]) => {
    if (selectedRole !== 'All' && selectedRole !== role) return null;
    if (users.length === 0) return null;

    return (
      <div key={role} className="role-section">
        <div className="role-section-header">
          <div className="role-section-title">
            <span className={`role-badge ${getRoleClass(role)}`}>
              {role}
            </span>
          </div>
          <span className="section-count">{users.length} users</span>
        </div>
        <table className="users-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>User{sortIcon('name')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{sortIcon('email')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('created')}>Created{sortIcon('created')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastLogin')}>Last Login{sortIcon('lastLogin')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortUsers(users).map(renderUserRow)}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">User Management</h1>
            <p className="page-header__subtitle">
              {isAdmin
                ? 'Manage users, roles, and permissions for the APR Framework'
                : 'View your user profile and credentials'}
            </p>
          </div>
          <div className="page-header__actions">
            {isAdmin && (
              <button
                onClick={handleResetPasswordOpen}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                🔑 Reset Password
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Controls Panel - Only show full controls to Administrators */}
      {isAdmin ? (
        <div className="controls-panel">
          <div className="filter-controls">
            <div className="role-tabs">
              {(['All', 'Administrator', 'Application Owner', 'APR Analyst'] as const).map(role => (
                <button
                  key={role}
                  className={`role-tab ${selectedRole === role ? 'active' : ''}`}
                  onClick={() => setSelectedRole(role)}
                >
                  {role === 'All' ? 'All Users' : role}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="🔍 Search by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px',
                fontSize: '14px', minWidth: '220px', outline: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="add-user-btn"
              onClick={() => setShowForm(true)}
            >
              ➕ Add New User
            </button>
            <button
              onClick={downloadUserData}
              style={{
                padding: '8px 16px', backgroundColor: 'transparent', color: '#28a745',
                border: '2px solid #28a745', borderRadius: '6px', fontSize: '14px',
                fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              📥 Download
            </button>
          </div>
        </div>
      ) : (
        <div className="controls-panel">
          <div className="filter-controls">
            <span style={{ color: '#666', fontSize: '14px' }}>
              Viewing your user profile
            </span>
          </div>
        </div>
      )}

      {/* Users Section */}
      <div className="users-section">
        <div className="section-header">
          <h2 className="section-title">
            📋 User Directory
            <span className="section-count">{loading ? 'Loading...' : `${filteredUsers.length} users`}</span>
          </h2>
        </div>

        {loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔄</div>
            <h3>Loading Users...</h3>
            <p>Fetching user data from database...</p>
          </div>
        ) : (
          <>
            {/* Locked Users Section - Always visible if there are locked users */}
            {isAdmin && lockedUsers.length > 0 && (
              <div className="role-section" style={{ marginBottom: '24px' }}>
                <div className="role-section-header" style={{ backgroundColor: '#fee2e2', borderLeft: '4px solid #dc2626' }}>
                  <div className="role-section-title">
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      🔒 Locked Users
                    </span>
                  </div>
                  <span className="section-count" style={{ color: '#dc2626' }}>{lockedUsers.length} locked</span>
                </div>
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Failed Attempts</th>
                      <th>Locked At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockedUsers.map(user => (
                      <tr key={user.id} style={{ backgroundColor: '#fef2f2' }}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
                              {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              <div style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                width: '14px',
                                height: '14px',
                                backgroundColor: '#dc2626',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px'
                              }}>🔒</div>
                            </div>
                            <div className="user-details">
                              <h4>{user.name}</h4>
                              <div className="user-id">User #{user.user_number}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <a href={`mailto:${user.email}`} className="user-email">
                            {user.email}
                          </a>
                        </td>
                        <td>
                          <span className={`role-badge ${getRoleClass(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span style={{ color: '#dc2626', fontWeight: '600' }}>
                            {(user as any).failed_login_attempts || 3} attempts
                          </span>
                        </td>
                        <td>
                          <div className="date-info">
                            {(user as any).locked_at ? formatDate((user as any).locked_at) : 'Unknown'}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => handleUnlockUser(user)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            title="Unlock this user account"
                          >
                            🔓 Unlock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedRole === 'All' ? (
              // Show all roles grouped
              <>
                {renderRoleSection('Administrator', groupedUsers.Administrator)}
                {renderRoleSection('Application Owner', groupedUsers['Application Owner'])}
                {renderRoleSection('APR Analyst', groupedUsers['APR Analyst'])}
              </>
            ) : (
              // Show filtered role
              renderRoleSection(selectedRole as UserRole, filteredUsers)
            )}

            {filteredUsers.length === 0 && !loading && (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <h3>No Users Found</h3>
                <p>
                  {selectedRole === 'All' 
                    ? 'No users have been created yet.' 
                    : `No users found with the role "${selectedRole}".`
                  }
                </p>
                <button 
                  className="btn-primary"
                  onClick={() => setShowForm(true)}
                >
                  Add First User
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={resetForm}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{padding: '24px'}}>
              <div style={{marginBottom: '32px'}}>
                <h3 style={{fontSize: '18px', fontWeight: '400', color: '#424242', margin: '0 0 16px 0'}}>User Information</h3>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px'}}>
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                      placeholder="e.g., John Doe"
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                      placeholder="e.g., john.doe@company.com"
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                    >
                      <option value="Application Owner">Application Owner</option>
                      <option value="APR Analyst">APR Analyst</option>
                      {currentUser?.role !== 'APR Analyst' && (
                        <option value="Administrator">Administrator</option>
                      )}
                    </select>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                    >
                      <option value="Active">✅ Active</option>
                      <option value="Inactive">❌ Inactive</option>
                    </select>
                  </div>

                  {!editingUser && (
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                      <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Password *</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                        placeholder="Enter password"
                      />
                    </div>
                  )}
                </div>
                {!editingUser && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#666',
                    marginTop: '8px'
                  }}>
                    ℹ️ User number will be auto-generated
                  </div>
                )}
              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e0e0e0'}}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 24px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#424242',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {editingUser ? '💾 Update User' : '➕ Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Update Modal */}
      {showEmailModal && emailEditUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={(e) => e.target === e.currentTarget && setShowEmailModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                📧 Update Email for {emailEditUser.name}
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>

            <div style={{padding: '24px'}}>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>New Email Address *</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                  placeholder="e.g., user@company.com"
                />
              </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0'}}>
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#424242',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEmailUpdate}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                📧 Update Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Update Modal */}
      {showPasswordModal && passwordEditUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={(e) => e.target === e.currentTarget && setShowPasswordModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                🔒 Change Password for {passwordEditUser.name}
              </h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>

            <div style={{padding: '24px'}}>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>New Password *</label>
                <div style={{position: 'relative'}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    style={{padding: '12px', paddingRight: '45px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white', width: '100%', boxSizing: 'border-box'}}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px',
                      color: '#757575'
                    }}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0'}}>
              <button
                type="button"
                onClick={() => setShowPasswordModal(false)}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#424242',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePasswordUpdate}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                🔒 Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={(e) => e.target === e.currentTarget && setShowResetPasswordModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                🔑 Reset Password
              </h2>
              <button
                onClick={() => setShowResetPasswordModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>

            <div style={{padding: '24px'}}>
              {/* Step indicator */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '24px',
                gap: '8px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: resetPasswordStep === 'email' ? '#f59e0b' : '#10b981',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {resetPasswordStep === 'email' ? '1' : '✓'}
                </div>
                <div style={{
                  width: '40px',
                  height: '2px',
                  backgroundColor: resetPasswordStep === 'email' ? '#e0e0e0' : '#10b981'
                }}></div>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: resetPasswordStep === 'password' ? '#f59e0b' : resetPasswordStep === 'confirm' ? '#10b981' : '#e0e0e0',
                  color: resetPasswordStep === 'email' ? '#757575' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {resetPasswordStep === 'confirm' ? '✓' : '2'}
                </div>
                <div style={{
                  width: '40px',
                  height: '2px',
                  backgroundColor: resetPasswordStep === 'confirm' ? '#f59e0b' : '#e0e0e0'
                }}></div>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: resetPasswordStep === 'confirm' ? '#f59e0b' : '#e0e0e0',
                  color: resetPasswordStep === 'confirm' ? 'white' : '#757575',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  3
                </div>
              </div>

              {/* Step 1: Email */}
              {resetPasswordStep === 'email' && (
                <div style={{display: 'flex', flexDirection: 'column', marginBottom: '16px'}}>
                  <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Step 1: Enter User Email *</label>
                  <input
                    type="email"
                    value={resetPasswordEmail}
                    onChange={(e) => {
                      setResetPasswordEmail(e.target.value);
                      setResetPasswordError('');
                    }}
                    required
                    style={{padding: '12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white'}}
                    placeholder="Enter user email"
                    autoFocus
                  />
                </div>
              )}

              {/* Step 2: Enter New Password */}
              {resetPasswordStep === 'password' && (
                <div style={{display: 'flex', flexDirection: 'column', marginBottom: '16px'}}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#0369a1'
                  }}>
                    Resetting password for: <strong>{resetPasswordEmail}</strong>
                  </div>
                  <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Step 2: Enter New Password *</label>
                  <div style={{position: 'relative'}}>
                    <input
                      type={showResetPassword ? "text" : "password"}
                      value={resetPasswordNewPassword}
                      onChange={(e) => {
                        setResetPasswordNewPassword(e.target.value);
                        setResetPasswordError('');
                      }}
                      required
                      style={{padding: '12px', paddingRight: '45px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white', width: '100%', boxSizing: 'border-box'}}
                      placeholder="Enter new password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '4px',
                        color: '#757575'
                      }}
                      title={showResetPassword ? "Hide password" : "Show password"}
                    >
                      {showResetPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm Password */}
              {resetPasswordStep === 'confirm' && (
                <div style={{display: 'flex', flexDirection: 'column', marginBottom: '16px'}}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#0369a1'
                  }}>
                    Resetting password for: <strong>{resetPasswordEmail}</strong>
                  </div>
                  <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Step 3: Confirm Password *</label>
                  <p style={{fontSize: '14px', color: '#666', margin: '0 0 12px 0'}}>
                    Please re-type the password to confirm it matches exactly.
                  </p>
                  <div style={{position: 'relative'}}>
                    <input
                      type={showResetConfirmPassword ? "text" : "password"}
                      value={resetPasswordConfirmPassword}
                      onChange={(e) => {
                        setResetPasswordConfirmPassword(e.target.value);
                        setResetPasswordError('');
                      }}
                      required
                      style={{padding: '12px', paddingRight: '45px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white', width: '100%', boxSizing: 'border-box'}}
                      placeholder="Re-type the password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '4px',
                        color: '#757575'
                      }}
                      title={showResetConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showResetConfirmPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              )}

              {resetPasswordError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '14px',
                  marginTop: '12px'
                }}>
                  {resetPasswordError}
                </div>
              )}
            </div>

            <div style={{display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0'}}>
              <div>
                {resetPasswordStep !== 'email' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordBack}
                    style={{
                      padding: '10px 24px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      color: '#424242',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    ← Back
                  </button>
                )}
              </div>
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  type="button"
                  onClick={() => setShowResetPasswordModal(false)}
                  style={{
                    padding: '10px 24px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#424242',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {resetPasswordStep === 'email' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordVerifyEmail}
                    disabled={!resetPasswordEmail.trim()}
                    style={{
                      padding: '10px 24px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: !resetPasswordEmail.trim() ? '#9ca3af' : '#f59e0b',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: !resetPasswordEmail.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                )}
                {resetPasswordStep === 'password' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordNext}
                    disabled={!resetPasswordNewPassword.trim()}
                    style={{
                      padding: '10px 24px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: !resetPasswordNewPassword.trim() ? '#9ca3af' : '#f59e0b',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: !resetPasswordNewPassword.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                )}
                {resetPasswordStep === 'confirm' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordSubmit}
                    disabled={!resetPasswordConfirmPassword.trim()}
                    style={{
                      padding: '10px 24px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: !resetPasswordConfirmPassword.trim() ? '#9ca3af' : '#10b981',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: !resetPasswordConfirmPassword.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🔑 Reset Password
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock User Modal */}
      {showUnlockModal && unlockUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={(e) => e.target === e.currentTarget && handleUnlockCancel()}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fef2f2'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#dc2626'}}>
                🔓 Unlock User Account
              </h2>
              <button
                onClick={handleUnlockCancel}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>

            <div style={{padding: '24px'}}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>User being unlocked:</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>{unlockUser.name}</div>
                <div style={{ fontSize: '14px', color: '#1976d2' }}>{unlockUser.email}</div>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#fff3cd',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#856404'
              }}>
                ⚠️ This account was locked due to multiple failed login attempts. Please set a new password for the user before unlocking.
              </div>

              <div style={{display: 'flex', flexDirection: 'column', marginBottom: '16px'}}>
                <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>New Password *</label>
                <div style={{position: 'relative'}}>
                  <input
                    type={showUnlockPassword ? "text" : "password"}
                    value={unlockNewPassword}
                    onChange={(e) => {
                      setUnlockNewPassword(e.target.value);
                      setUnlockError('');
                    }}
                    disabled={unlockIsLoading}
                    style={{
                      padding: '12px',
                      paddingRight: '45px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: unlockIsLoading ? '#f5f5f5' : 'white',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter new password for user"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUnlockPassword(!showUnlockPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px',
                      color: '#757575'
                    }}
                    title={showUnlockPassword ? "Hide password" : "Show password"}
                  >
                    {showUnlockPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div style={{display: 'flex', flexDirection: 'column', marginBottom: '16px'}}>
                <label style={{fontSize: '12px', fontWeight: '500', color: '#757575', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Confirm Password *</label>
                <div style={{position: 'relative'}}>
                  <input
                    type={showUnlockConfirmPassword ? "text" : "password"}
                    value={unlockConfirmPassword}
                    onChange={(e) => {
                      setUnlockConfirmPassword(e.target.value);
                      setUnlockError('');
                    }}
                    disabled={unlockIsLoading}
                    style={{
                      padding: '12px',
                      paddingRight: '45px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: unlockIsLoading ? '#f5f5f5' : 'white',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUnlockConfirmPassword(!showUnlockConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px',
                      color: '#757575'
                    }}
                    title={showUnlockConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showUnlockConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {unlockError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '14px',
                  marginTop: '12px'
                }}>
                  {unlockError}
                </div>
              )}
            </div>

            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0'}}>
              <button
                type="button"
                onClick={handleUnlockCancel}
                disabled={unlockIsLoading}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#424242',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: unlockIsLoading ? 'not-allowed' : 'pointer',
                  opacity: unlockIsLoading ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUnlockSubmit}
                disabled={unlockIsLoading || !unlockNewPassword.trim() || !unlockConfirmPassword.trim()}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: (unlockIsLoading || !unlockNewPassword.trim() || !unlockConfirmPassword.trim()) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (unlockIsLoading || !unlockNewPassword.trim() || !unlockConfirmPassword.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {unlockIsLoading ? 'Unlocking...' : '🔓 Unlock & Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UserManagement;